import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUserRoles } from '@/hooks/useUserRoles';

interface AdminCustomization {
  app_name: string | null;
  logo_url: string | null;
  font_family: string | null;
  primary_color: string | null;
  whistleblower_url: string | null;
}

export const useAdminCustomization = () => {
  const [customization, setCustomization] = useState<AdminCustomization | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { isSuperAdmin } = useUserRoles();

  const fetchCustomization = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('📱 No user found');
        return;
      }

      console.log('📱 Fetching customization for user:', user.id);

      // Use the database function to get customization (user's own or their creator's)
      const { data, error } = await supabase
        .rpc('get_user_admin_customization');

      console.log('📱 RPC Response:', { data, error });

      if (error) {
        console.error('❌ Error fetching admin customization:', error);
        setCustomization(null);
        return;
      }

      // RPC returns an array, take the first result
      const customizationData = data?.[0];

      if (customizationData) {
        console.log('✅ Fetched customization:', customizationData);
        setCustomization({
          app_name: customizationData.app_name,
          logo_url: customizationData.logo_url,
          font_family: customizationData.font_family,
          primary_color: customizationData.primary_color,
          whistleblower_url: customizationData.whistleblower_url,
        });
      } else {
        console.log('📱 No customization found, using defaults');
        setCustomization(null);
      }
    } catch (error) {
      console.error('❌ Exception fetching admin customization:', error);
      setCustomization(null);
    } finally {
      setLoading(false);
    }
  };

  const saveCustomization = async (
    appName: string, 
    logoUrl: string | null, 
    fontFamily?: string, 
    primaryColor?: string, 
    whistleblowerUrl?: string,
    targetAdminId?: string
  ): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Determine which admin to update
      const adminIdToUpdate = targetAdminId || user.id;

      // Only super admins can update other admins' customizations
      if (targetAdminId && targetAdminId !== user.id && !isSuperAdmin) {
        throw new Error('Only super admins can update other admins customizations');
      }

      // Build the update object - only include whistleblower_url if user is super admin
      const updateData: any = {
        admin_id: adminIdToUpdate,
        app_name: appName,
        logo_url: logoUrl,
        font_family: fontFamily || 'inter',
        primary_color: primaryColor || '221 83% 53%',
      };

      // Only super admins can update whistleblower_url
      if (isSuperAdmin && whistleblowerUrl !== undefined) {
        updateData.whistleblower_url = whistleblowerUrl;
      }

      const { error } = await supabase
        .from('admin_customizations')
        .upsert(updateData, {
          onConflict: 'admin_id'
        });

      if (error) throw error;

      // Only update local state if we're updating the current user's customization
      if (adminIdToUpdate === user.id) {
        const newCustomization = { 
          app_name: appName, 
          logo_url: logoUrl, 
          font_family: fontFamily || 'inter',
          primary_color: primaryColor || '221 83% 53%',
          whistleblower_url: (isSuperAdmin && whistleblowerUrl) 
            ? whistleblowerUrl 
            : (customization?.whistleblower_url || 'https://trust.shiftask.app/')
        };

        setCustomization(newCustomization);
      }

      toast({
        title: "Success",
        description: "Customization saved successfully",
      });

      return true;
    } catch (error: any) {
      console.error('Error saving customization:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save customization",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchCustomization();
  }, []);

  return {
    customization,
    loading,
    saveCustomization,
    refetch: fetchCustomization,
  };
};